import FastImage from '@d11/react-native-fast-image';
import React from 'react';
import { StyleSheet, TouchableOpacity, View } from 'react-native';
import { mvs } from 'react-native-size-matters';

import { HTMLContentRenderer } from './HTMLContentRenderer';
import { useAppSelector } from '../../app/hooks';
import { IS_ANDROID, isTablet } from '../../constants';
import { selectDivRadius } from '../../features/appconfig/store/selectors';
import { useAdjustedFontSize } from '../../hooks/useAdjustedFontSize';
import useOrientation from '../../hooks/useOrientation';
import { useIsEpisodeRead } from '../../hooks/useReadEpisodes';
import { formatEpisodeDate } from '../../localization/datetime';
import i18n from '../../localization/i18n';
import { PodcastEpisodeModel } from '../../models';
import { CustomIcon } from '../CustomIcon';
import CustomText from '../CustomText';
import { PlayPauseButton } from '../PlayPauseButton';

interface EpisodeCardProps {
  episode: PodcastEpisodeModel;
  isNew?: boolean;
  onPress?: () => void;
}

export type { EpisodeCardProps };

export const EpisodeCard: React.FC<EpisodeCardProps> = ({
  episode,
  isNew = false,
  onPress,
}) => {
  const isRead = useIsEpisodeRead(episode.podcastId, episode.id);
  const divRadius = useAppSelector(selectDivRadius);
  const thumbnailDynamicStyles = isRead ? { opacity: 0.5 } : { opacity: 1 };
  // landscape specific styles
  const orientation = useOrientation();
  const isTabletLandscape = isTablet && orientation === 'LANDSCAPE';
  const episodeTitleFontSize = useAdjustedFontSize(mvs(16));
  const dateFontSize = useAdjustedFontSize(mvs(12));
  const smallTextFontSize = useAdjustedFontSize(mvs(10));

  const handleEpisodePress = () => {
    if (onPress) {
      onPress();
    }
  };

  return (
    <View style={styles.container}>
      <View style={[styles.card, isTabletLandscape && styles.cardTabletLandscape]}>
        {/* left section with thumbnail */}
        <View style={styles.leftSection}>
          {/* episode thumbnail */}
          <TouchableOpacity
            style={[styles.thumbnailContainer, { borderRadius: mvs(divRadius) }]}
            onPress={handleEpisodePress}
          >
            <FastImage
              source={{ uri: episode.image, priority: FastImage.priority.normal }}
              style={[styles.thumbnail, thumbnailDynamicStyles]}
              resizeMode={FastImage.resizeMode.cover}
            />
          </TouchableOpacity>
        </View>

        {/* middle section with content */}
        <View style={styles.contentContainer}>
          <TouchableOpacity onPress={handleEpisodePress} activeOpacity={0.95}>
            {/* meta info */}
          <View style={styles.metaContainer}>
            <CustomText
              style={[styles.publishedAt, { fontSize: dateFontSize }]}
              weight="SemiBold"
              numberOfLines={1}
            >
              {formatEpisodeDate(episode.date)}
              {isNew && (
                <>
                  <CustomText
                    style={[styles.metaSeparator, { fontSize: smallTextFontSize }]}
                  >
                    {' • '}
                  </CustomText>
                  <CustomText style={[styles.newBadge, { fontSize: smallTextFontSize }]}>
                    {i18n.t('podcast.new')}
                  </CustomText>
                </>
              )}
              {isRead && (
                <>
                  <CustomText
                    style={[styles.metaSeparator, { fontSize: smallTextFontSize }]}
                  >
                    {' • '}
                  </CustomText>
                  <CustomText style={[styles.readBadge, { fontSize: smallTextFontSize }]}>
                    {i18n.t('podcast.read')}
                  </CustomText>
                </>
              )}
            </CustomText>
            {isRead && (
              <CustomIcon
                name="check"
                size={mvs(10)}
                color="rgba(37, 37, 37, 0.7)"
                style={styles.checkIcon}
              />
            )}
          </View>

          {/* episode title */}
          <CustomText
            style={[styles.episodeTitle, { fontSize: episodeTitleFontSize }]}
            weight="Bold"
            numberOfLines={isTablet ? 3 : 2}
          >
            {episode.title}
          </CustomText>

          {/* episode description - tablet only */}
          {isTablet && (
            <HTMLContentRenderer
              maxCharacters={100}
              content={episode.content || ''}
              disableStrongStyling={true}
            />
          )}
          </TouchableOpacity>

          {!isTabletLandscape && (
            <View style={styles.playButtonContainer}>
              <PlayPauseButton podcastEpisode={episode} iconSize={10} type="withDuration" />
              <View style={styles.playButtonSpacer} />
            </View>
          )}
        </View>

        {/* right section with duration badge - only show in tablet landscape */}
        {isTabletLandscape && (
          <View style={styles.rightSection}>
            <PlayPauseButton
              podcastEpisode={episode}
              iconSize={10}
              type="withDuration"
              style={{
                height: mvs(23),
              }}
            />
          </View>
        )}
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    width: '100%',
  },
  card: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: mvs(12),
    paddingVertical: mvs(10),
  },
  cardTabletLandscape: {
    alignItems: 'center',
  },
  leftSection: {
    position: 'relative',
  },
  thumbnailContainer: {
    width: mvs(75),
    height: mvs(75),
    overflow: 'hidden',
  },
  thumbnail: {
    width: '100%',
    height: '100%',
  },
  rightSection: {
    justifyContent: 'center',
    alignItems: 'flex-end',
  },
  contentContainer: {
    flex: 1,
    justifyContent: 'flex-start',
  },
  playButtonContainer: {
    flexDirection: 'row',
    marginTop: mvs(4),
  },
  playButtonSpacer: {
    flex: 1,
  },
  metaContainer: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  publishedAt: {
    color: 'rgba(37, 37, 37, 0.7)',
  },
  metaSeparator: {
    letterSpacing: mvs(2),
    color: 'rgba(37, 37, 37, 0.7)',
  },
  newBadge: {
    color: 'rgba(37, 37, 37, 0.7)',
  },
  readBadge: {
    color: 'rgba(37, 37, 37, 0.7)',
  },
  checkIcon: {
    marginLeft: mvs(2),
    marginTop: IS_ANDROID ? mvs(2.5) : mvs(1.5),
    color: 'rgba(37, 37, 37, 0.7)',
  },
  episodeTitle: {
    color: '#252525',
    lineHeight: mvs(20),
    marginBottom: mvs(2),
  },
});

export default EpisodeCard;
